import { useState, useRef } from 'react';

import { mergeChunks, encodeToWav } from '../../service/AudioService';
import { useEventCallback } from '../useEventCallback/useEventCallback';

interface Options<T = any> {
    recordedChunks: Float32Array[];
    onStart?: () => void;
    onEnd?: (blob: Blob, data?: T) => Promise<void> | void;
}

const defaultOptions: Options = {
    recordedChunks: [],
    onStart: undefined,
    onEnd: undefined,
};

export const useGetPartialRecording = <T = any>(props: Partial<Options<T>>) => {
    const options = { ...defaultOptions, ...props };
    const { recordedChunks, onStart, onEnd } = options;

    const [partialWavBlob, setPartialWavBlob] = useState<Blob | null>(null);
    const [isPartialActive, setIsPartialActive] = useState(false);

    const _onStart = useEventCallback(onStart);
    const _onEnd = useEventCallback(onEnd);

    const partialStartIndexRef = useRef<number>(0);

    const startPartialRecording = () => {
        // check ref instaed of state due to settimeout issues
        if (partialStartIndexRef.current) return;

        partialStartIndexRef.current = recordedChunks.length;
        setIsPartialActive(true);
        _onStart?.();
    };

    const stopPartialRecording = (data?: T) => {
        // check ref instaed of state due to settimeout issues
        if (!partialStartIndexRef.current) return;

        if (recordedChunks.length) {
            const partialChunks = recordedChunks.slice(partialStartIndexRef.current);

            const merged = mergeChunks(partialChunks);
            const blob = encodeToWav(merged);
            setPartialWavBlob(blob);
            _onEnd?.(blob, data as T);
        }

        partialStartIndexRef.current = 0; // Reset start index
        setIsPartialActive(false);
    };

    return {
        isPartialActive,
        partialWavBlob,
        startPartialRecording,
        stopPartialRecording,
    };
};
